import { Box, CircularProgress, Dialog, Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors, CommonModal, OtpInput, PasswordSetup, TextFieldComponent, theme } from '../../components';
import { default as ButtonComponent } from '../../components/button/ButtonComponent';
import { ContactSupportModalContent } from '../../components/contact-support/ContactSupportModalContent';
import { CommonLoginWrapper, LinkTextWrapper } from '../../components/global-styles/GlobalStyles';
import { BrandLogo } from '../../components/ui-components/BrandLogo';
import { emailValidation } from '../../components/validations/validations';
import {
    checkEmailForMigration,
    setPasswordForMigration,
    verifyOtpForMigration,
} from '../../services/migration-service';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast, resetToast } from '../../store/reducers/toastSlice';
import {
    trackAccountMigrationCompleted,
    trackAccountMigrationFailed,
    trackAccountMigrationStarted,
} from '../../utils/google-analytics';
import { defaultTimerCount, getMaskedEmailForProp } from '../../utils/helper';
import { isUserAlreadyMigrated, setCurrentDomainMigrationCookie } from '../../utils/migration-cookies';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';
import {
    FooterLinksContainer,
    FooterLinkText,
    FooterTypography,
    LoginLinkText,
    SeparatorText,
    SupportLinksStack,
    SupportText,
} from '../registration/registration-styles';

const UpgradeAnnouncement: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const migrationUserDetails = location?.state;
    const prefillEmailFromState = (location.state as { prefillEmail?: string } | null)?.prefillEmail?.trim() || '';
    const prefillEmailFromQuery = new URLSearchParams(location.search).get('email')?.trim() || '';
    const prefillMigrationEmail = prefillEmailFromState || prefillEmailFromQuery;
    const dispatch = useDispatch();
    const [isProceed, setIsProceed] = useState(false);
    const [email, setEmail] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [timerCount, setTimerCount] = useState(0);
    const [otpSuccess, setOtpSuccess] = useState(false);
    const [currentStep, setCurrentStep] = useState<'email' | 'password-reset' | 'password-success'>('email');
    const [userId, setUserId] = useState<number | null>(null);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const isBrandLoading = useSelector((state: RootState) => state.brand.isBrandLoading);
    const resolvedPortalId = (() => {
        const id = Number(brandDetails?.Id);
        return Number.isFinite(id) && id > 0 ? id : 2;
    })();
    const [open, setOpen] = useState(false);
    const handleOpenSupportModal = () => setOpen(true);
    const handleCloseSupportModal = () => setOpen(false);

    // Check if user has already migrated on component mount (runs on refresh as well).
    // Skip when registration sent us here with a prefilled email — API required migration; stale cookies must not bounce to login.
    useEffect(() => {
        if (isUserAlreadyMigrated() && !prefillMigrationEmail) {
            navigate(`${NavigationConfig.Login}?source=login`, { state: { fromMigration: true }, replace: true });
        }
    }, [navigate, prefillMigrationEmail]);

    // CRITICAL FIX: Enforce migration page access control on any change
    // This handles manual URL changes after migration is complete
    useEffect(() => {
        const checkAndEnforceAccess = () => {
            const isMigrated = isUserAlreadyMigrated();
            const hasPrefilledEmail = prefillMigrationEmail || prefillEmailFromQuery || prefillEmailFromState;
            const isComingFromValidSource = migrationUserDetails?.migrationUser || prefillMigrationEmail;

            // If user is migrated and NOT coming from registration/login with forced migration, redirect to login
            if (isMigrated && !hasPrefilledEmail && !isComingFromValidSource) {
                // User manually accessed migration page after completing migration - block access
                navigate(`${NavigationConfig.Login}?source=login`, { state: { fromMigration: true }, replace: true });
            }
        };

        // Run the check immediately and on every render
        checkAndEnforceAccess();
    }, [navigate, prefillMigrationEmail, prefillEmailFromQuery, prefillEmailFromState, migrationUserDetails]);

    useEffect(() => {
        if (migrationUserDetails?.migrationUser) {
            setIsProceed(true);
            setEmail(migrationUserDetails?.email || '');
            // Show informational toast when redirected from login
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: 'For Security Reasons, We Require You to Re-Register Your Account.',
                    severity: 'info',
                }),
            );
        }
    }, [migrationUserDetails, dispatch]);

    useEffect(() => {
        // If redirected from registration with migration-required response, take user directly
        // to the email verification step and prefill the entered email.
        if (!migrationUserDetails?.migrationUser && prefillMigrationEmail) {
            setIsProceed(true);
            setCurrentStep('email');
            setShowOtpModal(false);
            setEmail(prefillMigrationEmail);
            // Show informational toast when redirected from registration
            const isFromRegistration =
                (location.state as { forceMigrationFromRegistration?: boolean } | null)
                    ?.forceMigrationFromRegistration === true;
            if (isFromRegistration) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'For Security Reasons, We Require You to Re-Register Your Account.',
                        severity: 'info',
                    }),
                );
            }
        }
    }, [migrationUserDetails, prefillMigrationEmail, location, dispatch]);

    // FIX: Clear toast on component unmount to prevent toasts from persisting on other screens
    useEffect(() => {
        return () => {
            dispatch(resetToast({ show: false, message: null, isAlertOpen: false }));
        };
    }, [dispatch]);

    const handleProceed = () => {
        // Checkbox acknowledgement UI is currently hidden, so Proceed should always advance.
        setIsProceed(true);
    };

    const handleRegistration = () => {
        setEmail('');
        setShowOtpModal(false);
        setCurrentStep('email');
        setOtpSuccess(false);
        setUserId(null);
        setTimerCount(0);
        setIsProceed(false);
        navigate(`${NavigationConfig.Registration}?source=update_portal`, {
            state: { fromUpdatePortal: true },
            replace: false,
        });
    };

    const trimmedMigrationEmail = email.trim();
    const isMigrationEmailValid = trimmedMigrationEmail.length > 0 && emailValidation(trimmedMigrationEmail) === '';

    // Shared error-toast dispatch, reused by the catch blocks of the API calls below (they all
    // dispatch the same shape of toast, only the fallback message and whether a generic
    // `error.message` fallback is considered differ between call sites).
    const dispatchErrorToast = (message: string | undefined) => {
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message,
                severity: 'error',
            }),
        );
    };

    const buildApiErrorMessage = (error: unknown, fallbackMessage: string, includeGenericErrorMessage: boolean) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        return includeGenericErrorMessage
            ? err?.response?.data?.message || err?.message || fallbackMessage
            : err?.response?.data?.message || fallbackMessage;
    };

    const handleContinue = async () => {
        const trimmed = email.trim();
        if (!trimmed || emailValidation(trimmed) !== '') {
            return;
        }
        setEmail(trimmed);
        try {
            const response = await checkEmailForMigration({ email: trimmed, portalId: resolvedPortalId }, true);
            if (response?.data?.isExistingUser === true) {
                // TODO: User already has an account in the new system — redirect to login with prefilled email.
                // NoCookieGuard allows this because source=login and fromMigration=true are both checked.
                navigate(`${NavigationConfig.Login}?source=login`, {
                    state: {
                        fromMigration: true,
                        prefillEmail: trimmed,
                        toastMessage: t('registration.messages.existingUserMessage'),
                        toastSeverity: 'info',
                    },
                    replace: true,
                });
                return;
            } else if (response?.statusCode === 404 && response?.data?.isExistingUser === false) {
                // TODO: Email not found — redirect to registration with prefilled email and toast message.
                // Toast is passed via navigation state (not dispatched here) because the unmount
                // cleanup effect clears the toast store before the registration page renders.
                navigate(`${NavigationConfig.Registration}?source=update_portal`, {
                    state: {
                        fromUpdatePortal: true,
                        prefillEmail: trimmed,
                        toastMessage: response.message || t('migration.upgradeAnnouncement.toast.emailNotFound'),
                        toastSeverity: 'info',
                    },
                    replace: false,
                });
                return;
            } else if (response?.statusCode === 404 && response?.message) {
                // TODO: Other 404 errors (e.g. "No contracts found for this user to migrate") — show error, stay on page.
                trackAccountMigrationFailed('email_check', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message,
                        severity: 'error',
                    }),
                );
            } else if (response?.data?.isMigrated === false || response?.data?.isExistingUser === false) {
                // TODO: Fallback for non-404 responses where data flags indicate user is not registered/migrated.
                navigate(NavigationConfig.Registration);
                return;
            } else if (response.statusCode === 200 && response.data?.isMigrated === true) {
                trackAccountMigrationStarted();
                setShowOtpModal(true);
                setTimerCount(defaultTimerCount);
                setUserId(response.data?.id);
            } else {
                trackAccountMigrationFailed('email_check', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('migration.upgradeAnnouncement.toast.failedVerifyEmail'),
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            trackAccountMigrationFailed('email_check', error instanceof Error ? error.message : 'exception');
            dispatchErrorToast(
                buildApiErrorMessage(error, t('migration.upgradeAnnouncement.toast.invalidEmail'), true),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleVerifyOtp = async (otp: string) => {
        // Handle passcode verification
        if (!userId) {
            dispatchErrorToast(t('migration.upgradeAnnouncement.toast.userIdNotFound'));
            return;
        }

        try {
            // Call verify-otp API
            const response = await verifyOtpForMigration(
                {
                    id: userId,
                    email,
                    otp: parseInt(otp, 10),
                    portalId: resolvedPortalId,
                },
                true,
            );

            if (response.statusCode === 200) {
                setShowOtpModal(false);
                setOtpSuccess(true);
                setCurrentStep('password-reset');
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('migration.upgradeAnnouncement.toast.passcodeVerified'),
                        severity: 'success',
                    }),
                );
            } else {
                trackAccountMigrationFailed('otp_verify', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('migration.upgradeAnnouncement.toast.failedVerifyPasscode'),
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            trackAccountMigrationFailed('otp_verify', error instanceof Error ? error.message : 'exception');
            dispatchErrorToast(
                buildApiErrorMessage(error, t('migration.upgradeAnnouncement.toast.errorVerifyPasscode'), false),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleResendOTP = async () => {
        // Handle resend passcode
        try {
            const response = await checkEmailForMigration(
                {
                    email,
                    portalId: resolvedPortalId,
                },
                true,
            );

            if (response.statusCode === 200) {
                setTimerCount(defaultTimerCount); // Reset timer
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('migration.upgradeAnnouncement.toast.passcodeSent'),
                        severity: 'success',
                    }),
                );
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('migration.upgradeAnnouncement.toast.failedResendPasscode'),
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            dispatchErrorToast(
                buildApiErrorMessage(error, t('migration.upgradeAnnouncement.toast.errorResendPasscode'), false),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handlePasswordSetupComplete = async (password: string) => {
        // Handle password setup completion
        if (!userId) {
            dispatchErrorToast(t('migration.upgradeAnnouncement.toast.userIdNotFound'));
            return;
        }

        try {
            // Call set-password API
            const response = await setPasswordForMigration(
                {
                    id: userId,
                    email,
                    password,
                    portalId: resolvedPortalId,
                },
                true,
            );

            if (response.statusCode === 200) {
                trackAccountMigrationCompleted();
                // Set migration cookie for current domain
                setCurrentDomainMigrationCookie({
                    isMigrated: true,
                    Registered: true,
                });

                setCurrentStep('password-success');
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: t('migration.upgradeAnnouncement.toast.passwordResetSuccess'),
                        severity: 'success',
                    }),
                );

                // Auto-redirect to login after showing success message
                setTimeout(() => {
                    navigate(`${NavigationConfig.Login}?source=migration-complete`, {
                        state: { fromMigration: true, fromMigrationComplete: true },
                        replace: true,
                    });
                }, 2000); // 2 second delay to let user see success message
            } else {
                trackAccountMigrationFailed('password_setup', response.message);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || t('migration.upgradeAnnouncement.toast.failedSetPassword'),
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            console.error('Error setting password for migration:', error);
            trackAccountMigrationFailed('password_setup', error instanceof Error ? error.message : 'exception');
            dispatchErrorToast(
                buildApiErrorMessage(error, t('migration.upgradeAnnouncement.toast.errorSetPassword'), true),
            );
        } finally {
            dispatch(hideLoader());
        }
    };

    const handleGoToLogin = () => {
        // Navigate to login page
        navigate(`${NavigationConfig.Login}?source=login`, { state: { fromMigration: true }, replace: true });
    };

    const handleBackToEmail = () => {
        // Go back to email input
        setOtpSuccess(false);
        setCurrentStep('email');
        setShowOtpModal(false);
    };

    // Shared "―  or  ―" divider row - rendered identically (aside from outer spacing) on both
    // the initial announcement screen and the email verification screen.
    const orDividerRow = (sx: object) => (
        <Stack direction="row" alignItems="center" gap={1} sx={sx}>
            <Divider sx={{ borderColor: colors.greyPreset[20] }} />
            {t('migration.upgradeAnnouncement.dividerOr')}
            <Divider sx={{ borderColor: colors.greyPreset[20] }} />
        </Stack>
    );

    // Shared "Trouble activating? Contact support" row - rendered identically on both the
    // initial announcement screen and the email verification screen (only the top margin differs).
    const contactSupportRow = (mt?: number) => (
        <SupportText variant="body2" whiteSpace={{ xs: 'wrap', md: 'nowrap' }} mt={mt}>
            {t('registration.links.troubleActivation')}{' '}
            <LinkTextWrapper
                onClick={handleOpenSupportModal}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenSupportModal();
                    }
                }}
                tabIndex={0}
                role="button"
            >
                {t('registration.links.contactSupport')}
            </LinkTextWrapper>
        </SupportText>
    );

    // Shared Terms of Use / Privacy Policy footer links - rendered identically on both the
    // initial announcement screen and the email verification screen (only the outer wrapper differs).
    const footerTermsPrivacyLinks = (
        <FooterTypography variant="overline">
            <LinkTextWrapper sx={FooterLinkText} target="_blank" href="https://auxcap.com/terms-of-use/">
                {t('registration.links.termsOfUse')}
            </LinkTextWrapper>
            <SeparatorText variant="overline" color="textPrimary">
                {' | '}
            </SeparatorText>
            <LinkTextWrapper sx={FooterLinkText} target="_blank" href="https://auxcap.com/privacy-policy/">
                {t('registration.links.privacyPolicy')}
            </LinkTextWrapper>
        </FooterTypography>
    );

    // Shared "Already migrated? Login | Don't have an account? Register" row - rendered
    // identically on both the initial announcement screen and the email verification screen.
    const alreadyMigratedOrRegisterRow = (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="stretch"
            justifyContent="center"
            sx={{ width: '100%', gap: { xs: 2, sm: 0 } }}
        >
            <Stack alignItems="center" spacing={0.75} sx={{ flex: 1, px: { sm: 1 }, minWidth: 0 }}>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                    {t('migration.upgradeAnnouncement.alreadyMigrated')}
                </Typography>
                <LinkTextWrapper
                    onClick={() =>
                        navigate(`${NavigationConfig.Login}?source=login`, {
                            state: { fromMigration: true },
                            replace: true,
                        })
                    }
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`${NavigationConfig.Login}?source=login`, {
                                state: { fromMigration: true },
                                replace: true,
                            });
                        }
                    }}
                    tabIndex={0}
                    role="button"
                    sx={theme => ({ ...LoginLinkText(theme), fontWeight: 600 })}
                >
                    {t('registration.links.login')}
                </LinkTextWrapper>
            </Stack>
            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    borderColor: colors.greyPreset[30],
                    alignSelf: 'stretch',
                }}
            />
            <Divider
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    borderColor: colors.greyPreset[30],
                    width: '100%',
                }}
            />
            <Stack alignItems="center" spacing={0.75} sx={{ flex: 1, px: { sm: 1 }, minWidth: 0 }}>
                <Typography variant="body2" color="textSecondary" textAlign="center">
                    {t('registration.links.dontHaveAccount')}
                </Typography>
                <LinkTextWrapper
                    onClick={handleRegistration}
                    role="button"
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRegistration();
                        }
                    }}
                    tabIndex={0}
                    sx={theme => ({ ...LoginLinkText(theme), fontWeight: 600 })}
                >
                    {t('registration.links.register')}
                </LinkTextWrapper>
            </Stack>
        </Stack>
    );

    if (isBrandLoading) {
        return (
            <CommonLoginWrapper>
                <Stack
                    className="loginCard"
                    sx={{
                        maxWidth: '458px !important',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                    }}
                >
                    <CircularProgress size={36} thickness={3} sx={{ color: 'primary.main' }} />
                </Stack>
            </CommonLoginWrapper>
        );
    }

    return (
        <CommonLoginWrapper>
            <Stack
                className="loginCard"
                sx={{ maxWidth: `${currentStep === 'password-reset' ? '800px' : '458px'} !important` }}
            >
                <Stack sx={{ my: 3 }}>
                    <BrandLogo
                        alt="Auxcap logo"
                        style={{ height: '100%', width: '100%', maxHeight: 60, maxWidth: 188 }}
                    />
                </Stack>
                {!isProceed ? (
                    // Initial Announcement Screen
                    <Stack className="commonLoginCard loginCardBody" sx={{ textAlign: 'center' }}>
                        <Typography color="textPrimary" variant="h5">
                            {t('migration.upgradeAnnouncement.announcementTitle')}
                        </Typography>
                        <Typography color="textSecondary" variant="body1" textAlign={'center'} mt={1}>
                            {t('migration.upgradeAnnouncement.announcementBody1')}
                        </Typography>
                        <Typography color="textSecondary" variant="body1" textAlign={'center'} mt={1}>
                            {t('registration.messages.migrationRequiredBeforeRegistration')}
                        </Typography>
                        <Stack
                            my={2}
                            ml={1}
                            sx={{
                                '& .MuiFormControlLabel-root': {
                                    m: 0,
                                },
                            }}
                        >
                            {/* <FormControlLabel
                                control={
                                    <CustomCheckBox
                                        checked={isChecked}
                                        onChange={handleCheckboxChange}
                                    // borderColor={colors.greyPreset[50]}
                                    />
                                }
                                label={
                                    <Typography variant="body2" color="textSecondary" ml={1} textAlign={'start'}>
                                        {'Please Acknowledge  to Proceed to Self Service Portal.'}
                                    </Typography>
                                }
                                sx={{ alignItems: 'start' }}
                            /> */}
                        </Stack>
                        <Stack gap={2}>
                            <ButtonComponent
                                variant="contained"
                                // disabled={!isChecked}
                                onClick={handleProceed}
                            >
                                {t('migration.upgradeAnnouncement.clickToProceed')}
                            </ButtonComponent>
                            {orDividerRow({ '& .MuiDivider-root': { width: '45%' } })}
                            {alreadyMigratedOrRegisterRow}
                        </Stack>
                        <SupportLinksStack>
                            {contactSupportRow()}
                            {footerTermsPrivacyLinks}
                        </SupportLinksStack>
                    </Stack>
                ) : otpSuccess && currentStep === 'password-reset' ? (
                    // Password Reset Screen
                    <Stack className="commonLoginCard loginCardBody">
                        <PasswordSetup
                            forgotPassword={true}
                            onPasswordSetupComplete={handlePasswordSetupComplete}
                            onBack={handleBackToEmail}
                        />
                    </Stack>
                ) : currentStep === 'password-success' ? (
                    // Password Success Screen
                    <Stack className="commonLoginCard" sx={{ textAlign: 'center' }}>
                        <Typography textAlign={'center'} variant="h5" className="mainTitle">
                            {t('migration.upgradeAnnouncement.passwordSuccessTitle')}
                        </Typography>
                        <Typography variant="body2" color={colors.greyPreset[80]} mb={3} textAlign={'center'}>
                            {t('migration.upgradeAnnouncement.passwordSuccessBody')}
                        </Typography>
                        <ButtonComponent variant="contained" onClick={handleGoToLogin}>
                            {t('migration.upgradeAnnouncement.goToLogin')}
                        </ButtonComponent>
                        {/* Footer Links */}
                        <Box textAlign={'center'} mt={2}>
                            <Typography variant="body2" sx={{ color: colors.greyPreset[80] }}>
                                <LinkTextWrapper
                                    sx={{
                                        ...theme.typography.subtitle2,
                                    }}
                                    target="_blank"
                                    href="https://auxcap.com/terms-of-use/"
                                >
                                    {t('registration.links.termsOfUse')}
                                </LinkTextWrapper>
                                <Typography variant="overline" color="textPrimary" mx={'2px'}>
                                    {' | '}
                                </Typography>
                                <LinkTextWrapper
                                    sx={{
                                        ...theme.typography.subtitle2,
                                    }}
                                    target="_blank"
                                    href="https://auxcap.com/privacy-policy/"
                                >
                                    {t('registration.links.privacyPolicy')}
                                </LinkTextWrapper>
                            </Typography>
                        </Box>
                    </Stack>
                ) : (
                    // Email Verification Screen
                    <Stack className="commonLoginCard" sx={{ textAlign: 'center' }}>
                        <Typography color="textPrimary" variant="h5">
                            {t('migration.upgradeAnnouncement.emailStepTitle')}
                        </Typography>
                        <Typography color="textSecondary" variant="body1" textAlign={'center'} mt={1}>
                            {t('migration.upgradeAnnouncement.emailStepDescription')}
                        </Typography>
                        <Stack my={3} sx={{ textAlign: 'left' }}>
                            <TextFieldComponent
                                label={t('migration.upgradeAnnouncement.emailLabel')}
                                type="email"
                                onChange={e => setEmail(e.target.value)}
                                value={email}
                                required
                                maxLength={50}
                                autoComplete="off"
                            />
                        </Stack>
                        <ButtonComponent variant="contained" disabled={!isMigrationEmailValid} onClick={handleContinue}>
                            {t('registration.buttons.continue')}
                        </ButtonComponent>
                        <SupportLinksStack>
                            {orDividerRow({ width: '100%', '& .MuiDivider-root': { flex: 1 } })}
                            {alreadyMigratedOrRegisterRow}
                            {contactSupportRow(1)}
                            <FooterLinksContainer>{footerTermsPrivacyLinks}</FooterLinksContainer>
                        </SupportLinksStack>
                    </Stack>
                )}
            </Stack>
            {/* Passcode modal */}
            <Dialog
                open={showOtpModal}
                onClose={(event, reason) => {
                    // Only allow closing via close button, not backdrop click or escape key
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    setShowOtpModal(false);
                }}
                disableEscapeKeyDown
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        padding: 3,
                        maxWidth: '400px',
                        width: '100%',
                    },
                }}
                aria-labelledby="otp-dialog-title"
                aria-describedby="otp-dialog-description"
            >
                <OtpInput
                    mainTitle={t('migration.upgradeAnnouncement.otpTitle')}
                    email={email}
                    subtitle={t('migration.upgradeAnnouncement.otpSubtitle')
                        .replace(/\{\{length\}\}/g, '6')
                        .replace(/\{\{email\}\}/g, getMaskedEmailForProp(email))}
                    length={6}
                    onChange={(otp: string) => console.log(otp)}
                    onVerify={handleVerifyOtp}
                    verifyButtonText={t('migration.upgradeAnnouncement.verify')}
                    onClose={() => setShowOtpModal(false)}
                    onResend={handleResendOTP}
                    timerCount={timerCount}
                    resendTextWithTimer={t('migration.upgradeAnnouncement.resendPasscode')}
                    isCalledFromLogin={false}
                />
            </Dialog>

            {/* Contact Support Modal */}
            <CommonModal
                modalOpen={open}
                onCloseModal={handleCloseSupportModal}
                DialogMainTitle={t('registration.contactSupport.title')}
                showPrimaryButton={false}
                secondaryBtnName={t('migration.upgradeAnnouncement.gotIt')}
                secondaryBtnClick={handleCloseSupportModal}
                ModalMaxWidth={400}
            >
                <ContactSupportModalContent />
            </CommonModal>

            {/* Bookmark Portal Modal - Show on first visit */}
            {/* <BookmarkPortalModal open={showBookmarkModal} onClose={handleCloseBookmarkModal} /> */}
        </CommonLoginWrapper>
    );
};

export { UpgradeAnnouncement };
